import React from 'react';
import { Button } from '@/components/ui/button';
import { useSocialAuth } from '@/hooks/useSocialAuth';
import { cn } from '@/lib/utils';
import type { SocialProviderConfig, SocialProvider } from '@/services/interfaces/Auth';
import { getAppEnabledProviders } from '@/lib/auth/appConfig';

interface SocialLoginSectionProps {
  providers?: SocialProviderConfig[];
  onProviderClick?: (provider: SocialProvider) => void;
  className?: string;
  title?: string;
  showDivider?: boolean;
  dividerText?: string;
  disabled?: boolean;
}

export const SocialLoginSection: React.FC<SocialLoginSectionProps> = ({
  providers,
  onProviderClick,
  className,
  title,
  showDivider = true,
  dividerText = 'Or continue with',
  disabled = false,
}) => {
  const { isLoading, loginWithProvider } = useSocialAuth();

  // 使用应用特定的提供者配置（Apple 和 Google）
  const enabledProviders = providers || getAppEnabledProviders();

  const handleProviderClick = async (providerId: SocialProvider) => {
    if (disabled || isLoading) return;

    try {
      if (onProviderClick) {
        onProviderClick(providerId);
      } else {
        await loginWithProvider(providerId);
      }
    } catch (error) {
      console.error(`Failed to login with ${providerId}:`, error);
    }
  };

  if (enabledProviders.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {title && (
        <h3 className="text-lg font-semibold text-center">{title}</h3>
      )}

      {/* 社交登录按钮 - 使用原来的样式 */}
      <div className="grid grid-cols-2 gap-4">
        {enabledProviders.map((provider) => (
          <Button
            key={provider.id}
            variant="outline"
            type="button"
            className="w-full"
            onClick={() => handleProviderClick(provider.id)}
            disabled={disabled || isLoading}
          >
            {provider.id === 'apple' && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
                <path
                  d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                  fill="currentColor"
                />
              </svg>
            )}
            {provider.id === 'google-oauth2' && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="mr-2 h-4 w-4">
                <path
                  d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                  fill="currentColor"
                />
              </svg>
            )}
            {provider.name}
          </Button>
        ))}
      </div>

      {showDivider && (
        <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
          <span className="bg-card text-muted-foreground relative z-10 px-2">
            {dividerText}
          </span>
        </div>
      )}
    </div>
  );
};
